/**
 * A-10 — the audit log: every record change, order status move, handoff and Rx edit, merged into
 * one timeline.
 *
 * Built for a log that only grows: the server pages it by cursor (each page is "the next N after
 * the last one", as cheap deep in the log as at the top) and the virtual table asks for the next
 * page as it scrolls. One search box takes an order / Rx reference, a full ID (of a record or a
 * person), or words matched against the action and the actor's name; filters narrow by record
 * type, kind of entry, role and dates; the count stops at 10,000. The view lives in the URL, so it
 * can be reloaded or shared. An entry opens with its full detail — what changed, field by field —
 * and links to that record's whole history or that person's activity.
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import {
  auditCount,
  auditEntityTypes,
  auditTimeline,
  type AuditActorRole,
  type AuditEntry,
  type AuditFilter,
  type AuditSource,
} from "../../api/analytics";
import { describeError } from "../../api/errors";
import { ExportButton } from "../../components/ui/ExportButton";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  FilterField,
  SearchBox,
  SelectFilter,
} from "../../components/ui/FilterBar";
import { Modal } from "../../components/ui/Modal";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import type { TableColumn } from "../../lib/tableColumns";
import { sheetDateTime, type ExportColumn } from "../../lib/tableExport";
import { useSearchBox, useUrlParams } from "../../lib/urlState";
import { useNow } from "../../lib/useNow";

// Used until the API's list arrives (ADM-06: payment attempts, uploads, settings and others were
// missing, so their history couldn't be filtered).
const BUILT_IN_TYPES = [
  "data_deletion_request", "doctor", "medicine", "order", "payment_attempt", "pharmacy_settings",
  "prescription", "prescription_upload", "refund", "status_master", "user",
];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const SOURCE_LABEL: Record<AuditSource, string> = {
  audit_event: "Record change",
  order_status_history: "Order status",
  order_handoff_log: "Order handoff",
  prescription_audit: "Rx edit",
};
const SOURCES: { value: AuditSource | ""; label: string }[] = [
  { value: "", label: "All kinds" },
  { value: "audit_event", label: "Record changes" },
  { value: "order_status_history", label: "Order status" },
  { value: "order_handoff_log", label: "Order handoffs" },
  { value: "prescription_audit", label: "Rx edits" },
];
const ROLES: { value: AuditActorRole | ""; label: string }[] = [
  { value: "", label: "Anyone" },
  { value: "admin", label: "Admin" },
  { value: "pharmacist", label: "Pharmacist" },
  { value: "doctor", label: "Doctor" },
  { value: "patient", label: "Patient" },
  { value: "support", label: "Support" },
  { value: "system", label: "System" },
];
const ROLE_TONE: Record<string, string> = {
  admin: "bg-brand-50 text-brand",
  pharmacist: "bg-emerald-50 text-emerald-700",
  doctor: "bg-sky-50 text-sky-700",
  patient: "bg-violet-50 text-violet-700",
  support: "bg-amber-50 text-amber-700",
  system: "bg-gray-100 text-gray-600",
};
const ORDERS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
] as const;
const DEFAULT_SIZE = 50;
const ROW_H = 60;

// ---------------------------------------------------------------- wording

const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const typeLabel = (t: string) => titleCase(t.replace(/_/g, " "));
const shortId = (id: string) => `${id.slice(0, 8)}…`;
const text = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);

/** What happened, in words: "Order moved to Preparing", "Medicine updated". */
function describe(e: AuditEntry): string {
  if (e.source === "order_status_history") {
    const to = text(e.detail.to_status);
    const by = e.action.endsWith("auto.payment") ? "Payment moved the order" : e.action.endsWith("auto.system") ? "System moved the order" : "Order moved";
    return to ? `${by} to ${to}` : "Order status changed";
  }
  if (e.source === "order_handoff_log") {
    const to = text(e.detail.to_pharmacist);
    return to ? `Order handed to ${to}` : "Order handed over";
  }
  if (e.source === "prescription_audit") return "Prescription edited";
  return titleCase(e.action.split(/[._]/).filter(Boolean).join(" "));
}

// The export keeps the IDs and the raw details the table only shows on opening an entry.
const EXPORT_COLUMNS: ExportColumn<AuditEntry>[] = [
  { header: "When (IST)", value: (e) => sheetDateTime(e.at, true) },
  { header: "Kind", value: (e) => SOURCE_LABEL[e.source] ?? e.source },
  { header: "What happened", value: (e) => describe(e) },
  { header: "Record type", value: (e) => typeLabel(e.entity_type) },
  { header: "Record", value: (e) => e.entity_label ?? e.entity_id },
  { header: "Record ID", value: (e) => e.entity_id },
  { header: "By", value: (e) => e.actor_name ?? (e.actor_role === "system" ? "System" : null) },
  { header: "Role", value: (e) => titleCase(e.actor_role ?? "") },
  { header: "By (ID)", value: (e) => e.actor_user_id },
  { header: "Action", value: (e) => e.action },
  { header: "Details", value: (e) => (Object.keys(e.detail ?? {}).length ? JSON.stringify(e.detail) : "") },
];
// The audit API's largest page (500): a long timeline needs fewer round trips.
const EXPORT_PAGE = 500;

function when(iso: string, withSeconds = false): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
  });
}

function ago(iso: string, now: number): string {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days < 60 ? `${days} d ago` : `${Math.floor(days / 30)} mo ago`;
}

function RoleBadge({ role }: { role: string | null }) {
  if (!role) return null;
  return (
    <span className={`inline-flex w-fit shrink-0 rounded-full px-1.5 py-px text-[11px] font-medium ${ROLE_TONE[role] ?? "bg-gray-100 text-gray-600"}`}>
      {titleCase(role)}
    </span>
  );
}

// ---------------------------------------------------------------- page

const COLUMNS: TableColumn[] = [
  { key: "when", header: "When", width: { base: "6.5rem", sm: "8.5rem" } },
  { key: "what", header: "What happened", width: { base: "minmax(0,1fr)", lg: "minmax(0,1.6fr)" } },
  { key: "record", header: "Record", width: "minmax(0,1fr)", from: "md" },
  { key: "by", header: "By", width: { base: "minmax(0,0.9fr)", lg: "minmax(0,1fr)" }, from: "sm" },
];

export function AuditViewer() {
  const [params, setParam] = useUrlParams();
  const q = params.get("q") ?? "";
  const type = params.get("type") ?? "";
  const kind = (SOURCES.find((s) => s.value === params.get("kind"))?.value ?? "") as AuditSource | "";
  const role = (ROLES.find((r) => r.value === params.get("role"))?.value ?? "") as AuditActorRole | "";
  const from = DATE_RE.test(params.get("from") ?? "") ? params.get("from")! : "";
  const to = DATE_RE.test(params.get("to") ?? "") ? params.get("to")! : "";
  const entity = UUID_RE.test(params.get("entity") ?? "") ? params.get("entity")! : "";
  const actor = UUID_RE.test(params.get("actor") ?? "") ? params.get("actor")! : "";
  const newestFirst = params.get("order") !== "oldest";
  const size = [25, 50, 100].includes(Number(params.get("size"))) ? Number(params.get("size")) : DEFAULT_SIZE;
  const search = useSearchBox(q, (t) => setParam({ q: t }));
  const now = useNow();
  const [open, setOpen] = useState<AuditEntry | null>(null);

  const { data: types } = useQuery({ queryKey: ["audit-entity-types"], queryFn: auditEntityTypes, staleTime: 60 * 60_000 });
  const typeOptions = useMemo(
    () => [
      { value: "", label: "All records" },
      ...(types?.entity_types?.length ? types.entity_types : BUILT_IN_TYPES).map((t) => ({ value: t, label: typeLabel(t) })),
    ],
    [types],
  );

  const filter: AuditFilter = useMemo(() => {
    // A range typed back to front is read the right way round.
    const [lo, hi] = from && to && from > to ? [to, from] : [from, to];
    return {
      q: q || undefined,
      entity_type: type || undefined,
      source: kind || undefined,
      actor_role: role || undefined,
      entity_id: entity || undefined,
      actor_user_id: actor || undefined,
      // Whole days, in IST (the API reads a time without an offset as IST).
      since: lo ? `${lo}T00:00:00` : undefined,
      until: hi ? `${hi}T23:59:59.999` : undefined,
    };
  }, [q, type, kind, role, entity, actor, from, to]);

  const list = useInfiniteQuery({
    queryKey: ["audit", "list", filter, newestFirst, size],
    queryFn: ({ pageParam }) =>
      auditTimeline({ ...filter, order: newestFirst ? "newest" : "oldest", cursor: pageParam, limit: size }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // The previous rows stay (dimmed) while a new search loads, instead of the table blanking.
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.data) ?? [], [list.data]);
  const counted = useQuery({
    queryKey: ["audit", "count", filter],
    queryFn: () => auditCount(filter),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const totalLabel = counted.data
    ? `${counted.data.count.toLocaleString("en-IN")}${counted.data.capped ? "+" : ""}`
    : undefined;

  // The record / person a scope chip names, from any loaded entry about them.
  const entityName = entity ? (rows.find((r) => r.entity_id === entity)?.entity_label ?? shortId(entity)) : "";
  const actorName = actor ? (rows.find((r) => r.actor_user_id === actor)?.actor_name ?? shortId(actor)) : "";

  const narrowing = [type, kind, role, from || to, entity, actor].filter(Boolean).length;
  const activeFilters = narrowing + (q ? 1 : 0);
  const clearFilters = () => {
    search.cancel();
    setParam({ q: null, type: null, kind: null, role: null, from: null, to: null, entity: null, actor: null });
  };
  const scopeTo = (patch: Record<string, string | null>) => {
    search.cancel();
    setOpen(null);
    setParam(patch);
  };

  const index = open ? rows.findIndex((r) => r.id === open.id) : -1;
  const prev = index > 0 ? rows[index - 1] : undefined;
  const next = index >= 0 ? rows[index + 1] : undefined;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  const openEntry = (e: AuditEntry) => {
    const at = rows.findIndex((r) => r.id === e.id);
    // Near the end of what is loaded: fetch on, so Next keeps going.
    if (at >= rows.length - 3 && hasNextPage && !isFetchingNextPage) void fetchNextPage();
    setOpen(e);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Audit log</h2>
          <p className="text-sm text-gray-500">
            Every change to records, order status move, handoff and prescription edit. Open an entry for its details.
          </p>
        </div>
        {/* Every entry the search and filters match, in the list's order (cursor pages). */}
        <ExportButton
          table="audit entries"
          columns={EXPORT_COLUMNS}
          keyOf={(e) => e.id}
          fetchPage={async (cursor) => {
            const page = await auditTimeline({
              ...filter,
              order: newestFirst ? "newest" : "oldest",
              cursor: typeof cursor === "string" ? cursor : undefined,
              limit: EXPORT_PAGE,
            });
            return { rows: page.data, next: page.next_cursor ?? null, total: counted.data?.capped ? undefined : counted.data?.count };
          }}
        />
      </div>

      <FilterBar
        label="Search and filter the audit log"
        narrowing={narrowing}
        search={
          // Its own row from md up: the filters would otherwise squeeze it.
          <div className="flex min-w-[180px] flex-1 md:basis-full">
            <SearchBox
              value={search.text}
              onChange={search.onChange}
              placeholder="ORD-12, RX-3, an ID, an action or a person's name…"
            />
          </div>
        }
      >
        <SelectFilter label="Record" value={type} options={typeOptions} onChange={(v) => setParam({ type: v })} />
        <SelectFilter label="Kind" value={kind} options={SOURCES} onChange={(v) => setParam({ kind: v })} />
        <SelectFilter label="By" value={role} options={ROLES} onChange={(v) => setParam({ role: v })} />
        <FilterField label="From">
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setParam({ from: e.target.value })}
            className="rounded-lg border px-2 py-1"
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setParam({ to: e.target.value })}
            className="rounded-lg border px-2 py-1"
          />
        </FilterField>
        <SelectFilter
          label="Order"
          value={newestFirst ? "newest" : "oldest"}
          options={ORDERS}
          onChange={(v) => setParam({ order: v === "oldest" ? v : null })}
        />
        <BatchSizeFilter value={size} onChange={(n) => setParam({ size: n === DEFAULT_SIZE ? null : String(n) })} />
        {entity && <ScopeChip label="Record" text={entityName} onClear={() => setParam({ entity: null })} />}
        {actor && <ScopeChip label="Person" text={actorName} onClear={() => setParam({ actor: null })} />}
        <ClearFilters count={activeFilters} onClear={clearFilters} />
      </FilterBar>

      <div
        className={`flex min-h-0 flex-1 flex-col transition-opacity ${list.isPlaceholderData ? "opacity-60" : ""}`}
        aria-busy={list.isPlaceholderData}
      >
        <VirtualTable
          label="Audit log"
          columns={COLUMNS}
          rows={rows}
          rowKey={(e) => e.id}
          rowHeight={ROW_H}
          loading={list.isLoading}
          error={list.isError ? describeError(list.error) : null}
          onRetry={() => void list.refetch()}
          empty={activeFilters ? "No entries match these filters." : "Nothing has been logged yet."}
          total={counted.data?.count}
          totalLabel={totalLabel}
          hasMore={list.hasNextPage}
          loadingMore={list.isFetchingNextPage}
          loadMoreFailed={list.isFetchNextPageError}
          onLoadMore={list.fetchNextPage}
          resetKey={JSON.stringify([filter, newestFirst, size])}
          renderRow={(e) => <EntryRow e={e} now={now} active={e.id === open?.id} onOpen={() => openEntry(e)} />}
        />
      </div>

      <Modal open={!!open} title={open ? describe(open) : "Entry"} size="lg" onClose={() => setOpen(null)}>
        {open && (
          <>
            <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-4 flex items-center justify-between gap-2 border-b bg-surface px-5 py-2 text-sm">
              <button
                type="button"
                disabled={!prev}
                onClick={() => prev && openEntry(prev)}
                className="rounded-lg px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                ‹ {newestFirst ? "Newer" : "Older"}
              </button>
              <span className="text-xs text-gray-400">
                {index >= 0 ? `${(index + 1).toLocaleString("en-IN")} of ${totalLabel ?? rows.length}` : ""}
              </span>
              <button
                type="button"
                disabled={!next}
                onClick={() => next && openEntry(next)}
                className="rounded-lg px-2 py-1 font-medium text-brand hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {newestFirst ? "Older" : "Newer"} ›
              </button>
            </div>
            <EntryDetail
              key={open.id}
              e={open}
              now={now}
              onRecordHistory={
                open.entity_id && open.entity_id !== entity
                  ? () => scopeTo({ entity: open.entity_id, q: null, type: null, kind: null })
                  : undefined
              }
              onPersonActivity={
                open.actor_user_id && open.actor_user_id !== actor
                  ? () => scopeTo({ actor: open.actor_user_id, q: null, role: null })
                  : undefined
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
}

/** A filter set from an entry (a record or a person), shown with a way to drop it. */
function ScopeChip({ label, text, onClear }: { label: string; text: string; onClear: () => void }) {
  // A div, not a <label>: a click on the name would otherwise press the clear button.
  return (
    <div className="flex flex-col gap-1 text-xs text-gray-500">
      {label}
      <span className="inline-flex max-w-56 items-center gap-1 rounded-lg border border-brand bg-brand-50 py-1 pl-2.5 pr-1 text-brand">
        <span className="truncate">{text}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label={`Stop filtering by this ${label.toLowerCase()}`}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md hover:bg-white"
        >
          ×
        </button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- rows

function RecordName({ e }: { e: AuditEntry }) {
  if (!e.entity_id) return <span className="text-gray-400">—</span>;
  return e.entity_label ? <span>{e.entity_label}</span> : <span className="font-mono">{shortId(e.entity_id)}</span>;
}

function EntryRow({ e, now, active, onOpen }: { e: AuditEntry; now: number; active: boolean; onOpen: () => void }) {
  return (
    <div onClick={onOpen} className={`h-full cursor-pointer transition-colors ${active ? "bg-brand-50" : "hover:bg-gray-50"}`}>
      <TableRow
        columns={COLUMNS}
        className="h-full text-sm"
        cells={{
          when: (
            <div className="min-w-0 text-xs">
              <div className="truncate text-gray-700">{when(e.at)}</div>
              <div className="truncate text-gray-400">{ago(e.at, now)}</div>
            </div>
          ),
          what: (
            <div className="min-w-0">
              <button
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onOpen();
                }}
                className="block max-w-full truncate text-left font-medium text-gray-900 hover:text-brand hover:underline"
              >
                {describe(e)}
              </button>
              <div className="truncate text-xs text-gray-400">
                <span className="font-mono">{e.action}</span>
                {/* Narrow screens: the record and who, which have no column there. */}
                <span className="md:hidden">
                  {" · "}
                  {typeLabel(e.entity_type)} <RecordName e={e} />
                </span>
                <span className="sm:hidden"> · {e.actor_name ?? (e.actor_role ? titleCase(e.actor_role) : "System")}</span>
              </div>
            </div>
          ),
          record: (
            <div className="min-w-0 text-xs">
              <div className="truncate text-gray-400">{typeLabel(e.entity_type)}</div>
              <div className="truncate text-sm text-gray-700">
                <RecordName e={e} />
              </div>
            </div>
          ),
          by: (
            <div className="min-w-0">
              <div className="truncate text-gray-700">{e.actor_name ?? (e.actor_user_id ? shortId(e.actor_user_id) : "System")}</div>
              <RoleBadge role={e.actor_role} />
            </div>
          ),
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- one entry

function EntryDetail({
  e,
  now,
  onRecordHistory,
  onPersonActivity,
}: {
  e: AuditEntry;
  now: number;
  onRecordHistory?: () => void;
  onPersonActivity?: () => void;
}) {
  const d = e.detail;
  const facts: [string, ReactNode][] = [
    [
      "When",
      <>
        {new Date(e.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" })}{" "}
        <span className="text-gray-400">({ago(e.at, now)})</span>
      </>,
    ],
    [
      "Action",
      <>
        <span className="font-mono text-xs">{e.action}</span>
        <span className="ml-2 whitespace-nowrap rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
          {SOURCE_LABEL[e.source] ?? e.source}
        </span>
      </>,
    ],
    [
      "Record",
      <div className="space-y-0.5">
        <div>
          {typeLabel(e.entity_type)}
          {e.entity_label && <span className="font-medium"> · {e.entity_label}</span>}
        </div>
        {e.entity_id && <CopyId id={e.entity_id} />}
      </div>,
    ],
    [
      "By",
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          {e.actor_name ?? (e.actor_user_id ? "Unknown account" : "System")}
          <RoleBadge role={e.actor_role} />
        </div>
        {e.actor_user_id && <CopyId id={e.actor_user_id} />}
      </div>,
    ],
  ];
  if (e.source === "order_status_history") {
    if (text(d.to_status)) facts.push(["Moved to", text(d.to_status)]);
    if (text(d.remark)) facts.push(["Remark", text(d.remark)]);
  }
  if (e.source === "order_handoff_log") {
    facts.push(["Handed to", text(d.to_pharmacist) ?? (text(d.to_pharmacist_id) ? <CopyId id={String(d.to_pharmacist_id)} /> : "—")]);
  }
  if (text(d.reason)) facts.push(["Reason", text(d.reason)]);

  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-2">
        {facts.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3">
            <dt className="text-gray-400">{label}</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words text-gray-800">{value}</dd>
          </div>
        ))}
      </dl>

      {("before" in d || "after" in d) && <Changes before={d.before} after={d.after} />}

      {(onRecordHistory || onPersonActivity) && (
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row">
          {onRecordHistory && (
            <button type="button" onClick={onRecordHistory} className="rounded-lg border px-3 py-1.5 hover:bg-gray-50">
              This record's full history
            </button>
          )}
          {onPersonActivity && (
            <button type="button" onClick={onPersonActivity} className="rounded-lg border px-3 py-1.5 hover:bg-gray-50">
              Everything by {e.actor_name ?? "this person"}
            </button>
          )}
        </div>
      )}

      <details className="rounded-lg border">
        <summary className="cursor-pointer px-3 py-2 text-xs text-gray-500">Raw entry</summary>
        <pre className="max-h-72 overflow-auto border-t bg-gray-50 p-3 text-xs">{JSON.stringify(e, null, 2)}</pre>
      </details>
    </div>
  );
}

function CopyId({ id }: { id: string }) {
  const copy = () =>
    navigator.clipboard
      .writeText(id)
      .then(() => toast.success("ID copied."))
      .catch(() => toast.error("Couldn't copy: select the ID and copy it instead."));
  return (
    <span className="inline-flex max-w-full items-center gap-2">
      <span className="select-all break-all font-mono text-xs text-gray-500">{id}</span>
      <button type="button" onClick={copy} className="shrink-0 text-xs font-medium text-brand hover:underline">
        Copy
      </button>
    </span>
  );
}

// ---------------------------------------------------------------- what changed

const asFields = (v: unknown): Record<string, unknown> | null =>
  v === null || v === undefined ? null : typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : { value: v };
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function Value({ v }: { v: unknown }) {
  if (v === null || v === undefined || v === "") return <span className="text-gray-300">—</span>;
  if (typeof v === "object") return <span className="font-mono text-xs">{JSON.stringify(v)}</span>;
  return <>{String(v)}</>;
}

/** The record before and after, field by field; unchanged fields fold away. */
function Changes({ before, after }: { before: unknown; after: unknown }) {
  const [showAll, setShowAll] = useState(false);
  const b = asFields(before);
  const a = asFields(after);
  if (!b && !a) return null;
  const fields = [...new Set([...Object.keys(b ?? {}), ...Object.keys(a ?? {})])].sort();
  const rows = fields.map((f) => ({ field: f, before: b?.[f], after: a?.[f], changed: !same(b?.[f], a?.[f]) }));
  const changed = rows.filter((r) => r.changed);
  const shown = showAll ? rows : changed;
  const heading = !b ? "Created with" : !a ? "Removed; it had" : `${changed.length} ${changed.length === 1 ? "field" : "fields"} changed`;

  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-400">{heading}</h4>
        {rows.length > changed.length && (
          <button type="button" onClick={() => setShowAll((s) => !s)} className="text-xs text-brand hover:underline">
            {showAll ? "Only changes" : `Show all ${rows.length} fields`}
          </button>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="rounded-lg border px-3 py-2 text-gray-400">Nothing changed.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-1.5 font-medium">Field</th>
                {b && <th className="px-3 py-1.5 font-medium">Before</th>}
                {a && <th className="px-3 py-1.5 font-medium">After</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {shown.map((r) => (
                <tr key={r.field} className="align-top">
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-gray-500">{r.field}</td>
                  {b && (
                    <td className={`break-all px-3 py-1.5 ${r.changed && a ? "bg-red-50/60 text-red-800" : "text-gray-700"}`}>
                      <Value v={r.before} />
                    </td>
                  )}
                  {a && (
                    <td className={`break-all px-3 py-1.5 ${r.changed && b ? "bg-emerald-50/60 text-emerald-800" : "text-gray-700"}`}>
                      <Value v={r.after} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
